// components/parcours-client/VehicleStep.js
import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  TextField,
} from "@mui/material";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { useTranslation } from "react-i18next";

const VehicleStep = ({ formData, setFormData, handleNext }) => {
  const { t } = useTranslation("parcours-client/VehicleStep");
  const [errors, setErrors] = useState({});

  // ✅ Initialise la date du jour au premier rendu si vide
  useEffect(() => {
    if (!formData.dateAchat) {
      const today = new Date().toISOString().split("T")[0]; // YYYY-MM-DD
      setFormData((prev) => ({ ...prev, dateAchat: today }));
    }
  }, [formData.dateAchat, setFormData]);

  const validate = () => {
    const req = t("errors.required");
    const newErrors = {};
    if (!formData.bikeType) newErrors.bikeType = req;
    if (!formData.marque) newErrors.marque = req;
    if (!formData.modele) newErrors.modele = req;
    if (!formData.prix) newErrors.prix = req;
    if (!formData.dateAchat) newErrors.dateAchat = req;
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validate()) handleNext();
  };

  const customFieldStyle = {
    "& .MuiOutlinedInput-root": {
      "& fieldset": { borderColor: "#4eb933" },
      "&:hover fieldset": { borderColor: "#4eb933" },
      "&.Mui-focused fieldset": { borderColor: "#4eb933" },
    },
  };

  const vehicleTypes = t("vehicleTypes", { returnObjects: true });

  return (
    <Box sx={{ display: "flex", justifyContent: "center", px: 2, mt: 6 }}>
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          maxWidth: "800px",
          borderRadius: "20px",
          px: { xs: 3, md: 6 },
          pt: { xs: 4, md: 6 },
          pb: { xs: 4, md: 5 },
          backgroundColor: "#F8F8F8",
          fontFamily: "'Poppins', sans-serif",
        }}
      >
        <Typography
          sx={{
            fontWeight: 900,
            fontSize: { xs: "2rem", md: "2.6rem" },
            color: "#000",
            mb: 3,
            textAlign: "center",
          }}
        >
          {t("title.part1")}{" "}
          <Box component="span" sx={{ color: "#4eb933" }}>
            {t("title.highlight")}
          </Box>
        </Typography>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField
            select
            fullWidth
            label={t("fields.type")}
            value={formData.bikeType ?? ""}
            onChange={(e) => setFormData({ ...formData, bikeType: e.target.value })}
            error={!!errors.bikeType}
            helperText={errors.bikeType}
            SelectProps={{ native: true }}
            sx={customFieldStyle}
          >
            {/* Option vide pour l'état non sélectionné */}
            <option value="" aria-hidden="true" />
            {vehicleTypes.map((label, idx) => (
              <option key={idx} value={label}>
                {label}
              </option>
            ))}
          </TextField>

          <TextField
            fullWidth
            label={t("fields.brand")}
            value={formData.marque || ""}
            onChange={(e) => setFormData({ ...formData, marque: e.target.value })}
            error={!!errors.marque}
            helperText={errors.marque}
            sx={customFieldStyle}
          />

          <TextField
            fullWidth
            label={t("fields.model")}
            value={formData.modele || ""}
            onChange={(e) => setFormData({ ...formData, modele: e.target.value })}
            error={!!errors.modele}
            helperText={errors.modele}
            sx={customFieldStyle}
          />

          <TextField
            fullWidth
            label={t("fields.price")}
            type="text"
            inputProps={{ inputMode: "decimal", pattern: "[0-9]*[.,]?[0-9]*" }}
            value={formData.prix || ""}
            onChange={(e) => {
              const val = e.target.value.replace(",", "."); // accepte virgule comme point
              if (/^\d*\.?\d*$/.test(val)) {
                setFormData({ ...formData, prix: val });
              }
            }}
            error={!!errors.prix}
            helperText={errors.prix}
            sx={customFieldStyle}
          />

          <TextField
            fullWidth
            label={t("fields.purchaseDate")}
            type="date"
            InputLabelProps={{ shrink: true }}
            value={formData.dateAchat || ""}
            onChange={(e) => setFormData({ ...formData, dateAchat: e.target.value })}
            error={!!errors.dateAchat}
            helperText={errors.dateAchat}
            sx={customFieldStyle}
          />
        </Box>

        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 4 }}>
          <Button
            variant="contained"
            onClick={handleSubmit}
            endIcon={<ArrowForwardIosIcon sx={{ fontSize: 16 }} />}
            sx={{
              backgroundColor: "#4eb933",
              color: "#fff",
              "&:hover": { backgroundColor: "#3e9e2a" },
              fontWeight: 700,
              borderRadius: "12px",
              textTransform: "none",
              fontSize: "1rem",
              py: 1.2,
              px: 3,
              boxShadow: "0 4px 10px rgba(0, 0, 0, 0.1)",
            }}
          >
            {t("buttons.next")}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default VehicleStep;